import { useEffect, useState } from 'react';
import Logo from './Logo';
import OutboundTokens from './OutboundTokens';
import InboundTokens from './InboundTokens';
import ManagementKeys from './ManagementKeys';
import { ApiError, createStripePortal, fetchMe, type CurrentUser } from '../lib/api';

export default function Dashboard() {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    fetchMe()
      .then((currentUser) => {
        if (!currentUser.hasActiveSubscription) {
          window.location.replace('/checkout');
          return;
        }
        setUser(currentUser);
      })
      .catch((caught) => {
        if (caught instanceof ApiError && caught.status === 401) {
          window.location.href = '/_auth/google/login';
          return;
        }
        setError(caught?.message ?? 'Could not load your account.');
      });
  }, []);

  async function openBillingPortal() {
    setPortalLoading(true);
    try {
      const { url } = await createStripePortal();
      window.location.assign(url);
    } catch (caught) {
      setPortalLoading(false);
      setError(caught instanceof Error ? caught.message : 'Could not open billing portal.');
    }
  }

  if (error) {
    return <FullPageState title="// ERROR" body={error} />;
  }
  if (!user) {
    return <FullPageState title="// LOADING" body="One moment — fetching ledger…" />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b-2 border-border bg-background relative z-10">
        <div className="container-ledger flex h-16 items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <Logo />
            <span className="font-display text-base uppercase tracking-tight">Key Receipt</span>
          </a>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-[11px] uppercase tracking-wider font-bold text-muted-foreground font-mono">
              {user.email}
            </span>
            <button
              type="button"
              onClick={openBillingPortal}
              disabled={portalLoading}
              className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground hover:text-foreground transition-colors disabled:opacity-60"
            >
              {portalLoading ? 'Opening…' : 'Billing'}
            </button>
            <a
              href="/_auth/logout"
              className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground hover:text-foreground transition-colors"
            >
              Sign out
            </a>
          </div>
        </div>
      </header>

      <main className="flex-1 container-ledger py-12 md:py-16">
        <div className="mb-10">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-spray-magenta mb-3 font-bold">
            // Dashboard · {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </div>
          <h1 className="font-display text-4xl md:text-5xl uppercase leading-[0.95]">
            Welcome back
            {user.name ? <>, <span className="text-spray-yellow">{user.name.split(' ')[0]}</span></> : ''}.
          </h1>
          <p className="mt-5 text-muted-foreground max-w-2xl leading-relaxed">
            Add the upstream credentials you already pay for, then mint one inbound
            token per client. Upstream keys are encrypted at rest; inbound tokens
            are shown once and never again.
          </p>
          {user.subscriptionStatus && <SubscriptionBadge user={user} />}
        </div>

        <div className="grid grid-cols-1 gap-8">
          <OutboundTokens />
          <InboundTokens />
          <ManagementKeys />
        </div>
      </main>

      <footer className="border-t-2 border-border bg-card/50 mt-16">
        <div className="container-ledger py-6 text-[11px] uppercase tracking-wider text-muted-foreground flex flex-col sm:flex-row justify-between gap-2">
          <span>Key Receipt · keyreceipt.com</span>
          <a
            href="mailto:hi@keyreceipt.com"
            className="hover:text-spray-yellow transition-colors font-mono"
          >
            hi@keyreceipt.com
          </a>
        </div>
      </footer>
    </div>
  );
}

function SubscriptionBadge({ user }: { user: CurrentUser }) {
  const statusLabel =
    user.subscriptionStatus === 'trialing'
      ? 'Free trial'
      : user.subscriptionStatus === 'canceling'
        ? 'Cancels at period end'
        : user.subscriptionStatus === 'past_due'
          ? 'Payment past due'
          : user.subscriptionStatus === 'active'
            ? 'Active'
            : null;
  if (!statusLabel) return null;

  const periodEnd = user.trialEnd ?? user.currentPeriodEnd;
  const renews = periodEnd
    ? new Date(periodEnd * 1000).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const stateColor =
    user.subscriptionStatus === 'past_due' ? 'border-destructive text-destructive' : 'border-spray-yellow text-spray-yellow';

  return (
    <div
      className={`mt-5 inline-flex items-center gap-2 text-[10px] font-mono tracking-[0.18em] uppercase font-bold border-2 px-3 py-1.5 ${stateColor}`}
    >
      <span aria-hidden className="w-1.5 h-1.5 bg-current inline-block" />
      {statusLabel}
      {user.plan && <span className="text-muted-foreground"> · {user.plan}</span>}
      {renews && (
        <span className="text-muted-foreground normal-case tracking-normal">
          {user.subscriptionStatus === 'trialing' ? 'ends' : 'renews'} {renews}
        </span>
      )}
    </div>
  );
}

function FullPageState({ title, body }: { title: string; body: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="block-brutal p-8 max-w-md text-center">
        <Logo className="h-10 w-10 mx-auto mb-4 text-base" />
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-spray-magenta font-bold mb-3">
          {title}
        </div>
        <p className="text-foreground leading-relaxed">{body}</p>
        <a
          href="/"
          className="mt-5 inline-block text-[11px] uppercase tracking-wider font-bold text-spray-yellow hover:underline"
        >
          ← Back home
        </a>
      </div>
    </div>
  );
}
